import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { testCaseService } from '@/database/services'
import { useAuthStore } from './auth'

export const useTestCaseStore = defineStore('testCase', () => {
  // State
  const testCases = ref([])
  const loading = ref(false)
  const error = ref(null)
  
  // Get auth store
  const authStore = useAuthStore()

  // Getters
  const getTestCases = computed(() => testCases.value)
  const getLoading = computed(() => loading.value)
  const getError = computed(() => error.value)

  // Actions
  const fetchTestCases = async (filters = {}) => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.getAll(filters)
      testCases.value = data
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test cases:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchTestCaseById = async (id) => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.getById(id)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test case:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const createTestCase = async (testCaseData) => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.create(testCaseData)
      testCases.value.push(data)
      
      // Create activity entry for test case creation
      try {
        const isCloned = testCaseData.cloned_from && testCaseData.cloned_from_name
        const activityData = {
          type: isCloned ? 'cloned' : 'created',
          title: isCloned ? 'Test case cloned' : 'Test case created',
          description: isCloned 
            ? `Test case "${data.name}" was cloned from "${testCaseData.cloned_from_name}"`
            : `Test case "${data.name}" was created`,
          details: {
            'Test Case ID': data.testCaseId || data.id,
            'Type': data.type || 'N/A',
            'Priority': data.priority || 'N/A',
            'Status': data.status || 'N/A',
            ...(isCloned && {
              'Cloned From': testCaseData.cloned_from_name,
              'Original Test Case ID': testCaseData.cloned_from
            })
          },
          created_by: data.created_by,
          project_id: data.project_id,
          timestamp: new Date()
        }
        
        // Create activity in activities collection
        await createTestCaseActivity(data.id, activityData)
        
        // Add activity to test case's embedded activities array
        const activityForTestCase = {
          id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: activityData.type,
          title: activityData.title,
          description: activityData.description,
          details: activityData.details,
          created_by: data.created_by,
          timestamp: new Date()
        }
        
        // Update the test case with the activity
        await testCaseService.update(data.id, {
          activities: [...(data.activities || []), activityForTestCase]
        })
        
      } catch (activityError) {
        console.error('Failed to create activity entry:', activityError)
        // Don't throw error for activity creation failure
      }
      
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error creating test case:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateTestCase = async (id, testCaseData) => {
    try {
      loading.value = true
      error.value = null
      
      
      // Get the original test case for comparison - try to fetch it if not in local array
      let originalTestCase = testCases.value.find(tc => tc.id === id)
      if (!originalTestCase) {
        try {
          // Handle both display ID and database ID lookup
          if (id.startsWith('TC')) {
            // Try to find by testCaseId (display ID)
            const projectTestCases = await testCaseService.getByProject(testCaseData.project_id)
            originalTestCase = projectTestCases.find(tc => tc.testCaseId === id)
            if (!originalTestCase) {
              // Fallback to direct ID lookup
              originalTestCase = await testCaseService.getById(id)
            }
          } else {
            // Direct ID lookup for database IDs
            originalTestCase = await testCaseService.getById(id)
          }
        } catch (error) {
          console.warn('Could not fetch original test case:', error)
          originalTestCase = null
        }
      } else {
      }
      
      const data = await testCaseService.updateTestCase(id, testCaseData)
      const index = testCases.value.findIndex(tc => tc.id === id)
      if (index !== -1) {
        testCases.value[index] = data
      }
      
      // Note: Activity tracking for field updates is handled by TestCaseService.updateTestCase()
      // which creates specific field_updated activities for each changed field.
      // We don't need to create a generic "updated" activity here as it would be redundant.
      
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error updating test case:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteTestCase = async (id, deletedBy = null, deletedByName = null) => {
    try {
      loading.value = true
      error.value = null
      
      // Get current user from authStore if not provided
      let currentUser = deletedBy
      let userName = deletedByName
      if (!currentUser) {
        try {
          currentUser = authStore.user?.id || authStore.user?.uid
          userName = userName || authStore.user?.name || authStore.user?.email || 'Unknown'
        } catch (authError) {
          console.warn('Could not get user from authStore:', authError)
          currentUser = 'unknown_user'
          userName = 'Unknown'
        }
      }
      
      await testCaseService.softDelete(id, currentUser, userName)
      testCases.value = testCases.value.filter(tc => tc.id !== id)
      return true
    } catch (err) {
      error.value = err.message
      console.error('Error deleting test case:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const restoreTestCase = async (id, restoredBy = null, restoredByName = null) => {
    try {
      loading.value = true
      error.value = null
      
      // Get current user from authStore if not provided
      let currentUser = restoredBy
      let userName = restoredByName
      if (!currentUser) {
        try {
          currentUser = authStore.user?.id || authStore.user?.uid
          userName = userName || authStore.user?.name || authStore.user?.email || 'Unknown'
        } catch (authError) {
          console.warn('Could not get user from authStore:', authError)
          currentUser = 'unknown_user'
          userName = 'Unknown'
        }
      }
      
      const restoredTestCase = await testCaseService.restore(id, currentUser, userName)
      testCases.value.push(restoredTestCase)
      return restoredTestCase
    } catch (err) {
      error.value = err.message
      console.error('Error restoring test case:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const getDeletedTestCases = async (projectId) => {
    try {
      loading.value = true
      error.value = null
      const deletedTestCases = await testCaseService.getDeletedByProject(projectId)
      return deletedTestCases
    } catch (err) {
      error.value = err.message
      console.error('Error getting deleted test cases:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const permanentDeleteTestCase = async (id) => {
    try {
      loading.value = true
      error.value = null
      await testCaseService.permanentDelete(id)
      return true
    } catch (err) {
      error.value = err.message
      console.error('Error permanently deleting test case:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchTestCasesByProject = async (projectId) => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.getByProject(projectId)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test cases by project:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchTestCasesByStatus = async (projectId, status) => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.getByStatus(projectId, status)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test cases by status:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchTestCasesByType = async (projectId, type) => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.getByType(projectId, type)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test cases by type:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const searchTestCases = async (projectId, searchTerm) => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.search(projectId, searchTerm)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error searching test cases:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  const clearTestCases = () => {
    testCases.value = []
  }

  const getNextTestCaseId = async () => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.getNextTestCaseId()
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error getting next test case ID:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const getNextTestCaseIds = async (count) => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.getNextTestCaseIds(count)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error getting next test case IDs:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const assignTestCaseIds = async () => {
    try {
      loading.value = true
      error.value = null
      const data = await testCaseService.assignTestCaseIds()
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error assigning test case IDs:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  // Convenience methods that match what views expect
  const getTestCasesAsync = async (filters = {}) => {
    return await fetchTestCases(filters)
  }

  const getTestCase = async (id) => {
    return await fetchTestCaseById(id)
  }

  // Activity tracking methods
  const getTestCaseActivities = async (testCaseId) => {
    try {
      const data = await testCaseService.getActivities(testCaseId)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error fetching test case activities:', err)
      throw err
    }
  }

  const createTestCaseActivity = async (testCaseId, activityData) => {
    try {
      const data = await testCaseService.createActivity(testCaseId, activityData)
      return data
    } catch (err) {
      error.value = err.message
      console.error('Error creating test case activity:', err)
      throw err
    }
  }

  const addTestRunHistory = async (testCaseId, testRunData) => {
    try {
      const result = await testCaseService.addTestRunHistory(testCaseId, testRunData)
      return result
    } catch (error) {
      console.error('Error adding test run history:', error)
      throw error
    }
  }

  const getTestRunHistory = async (testCaseId) => {
    try {
      const result = await testCaseService.getTestRunHistory(testCaseId)
      return result
    } catch (error) {
      console.error('Error getting test run history:', error)
      throw error
    }
  }

  return {
    // State
    testCases,
    loading,
    error,
    // Getters
    getTestCases, // computed getter
    getLoading,
    getError,
    // Actions
    fetchTestCases,
    fetchTestCaseById,
    createTestCase,
    updateTestCase,
    deleteTestCase,
    restoreTestCase,
    getDeletedTestCases,
    permanentDeleteTestCase,
    fetchTestCasesByProject,
    fetchTestCasesByStatus,
    fetchTestCasesByType,
    searchTestCases,
    clearError,
    clearTestCases,
    getNextTestCaseId,
    getNextTestCaseIds,
    assignTestCaseIds,
    // Activity methods
    getTestCaseActivities,
    createTestCaseActivity,
    // Test run history methods
    addTestRunHistory,
    getTestRunHistory,
    // Convenience methods - aliased to avoid duplicate key warnings
    fetchTestCasesAsync: getTestCasesAsync, // async method for API calls
    getTestCaseById: getTestCase
  }
})
